<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const stationFields = [
  { field: 'Nama Stasiun', type: 'Text', wajib: '✅ Ya', desc: 'Nama yang tampil di layar dapur & daftar stasiun', example: '"Dapur Utama", "Barista"' },
  { field: 'Tipe', type: 'Dropdown', wajib: '✅ Ya', desc: 'Kategori stasiun: Dapur (Kitchen), Minuman (Beverage), Dessert, atau Lainnya', example: '"Dapur (Kitchen)"' },
  { field: 'Printer', type: 'Dropdown', wajib: '❌ Tidak', desc: 'Printer dapur yang akan otomatis cetak struk pesanan untuk stasiun ini (butuh Modul Printer sudah disetel)', example: '"Printer Dapur 1"' },
  { field: 'Default', type: 'Toggle', wajib: '❌ Tidak', desc: 'Stasiun ini jadi tujuan otomatis untuk produk yang belum di-assign ke stasiun tertentu', example: 'Aktif' },
  { field: 'Urutan', type: 'Angka', wajib: '❌ Tidak', desc: 'Urutan tampil di daftar stasiun & tab pemilih layar dapur', example: '1, 2, 3, ...' },
  { field: 'Aktif', type: 'Toggle', wajib: '❌ Tidak', desc: 'Nonaktifkan kalau stasiun sedang tidak dipakai, tanpa menghapus datanya', example: 'Aktif' },
]

const faq = [
  {
    q: 'Berapa banyak stasiun yang sebaiknya saya buat?',
    a: 'Tergantung ukuran dapur Anda. Untuk usaha kecil, 1 stasiun "Dapur Utama" saja sudah cukup. Kalau ada pembagian kerja jelas (mis. barista terpisah dari juru masak), buat 2-3 stasiun agar masing-masing tim hanya melihat pesanan yang jadi tanggung jawabnya.',
  },
  {
    q: 'Apakah saya bisa menghapus stasiun yang sudah pernah dipakai?',
    a: 'Sebaiknya jangan dihapus kalau sudah ada pesanan yang tercatat menggunakan stasiun tersebut. Matikan saja lewat toggle "Aktif" — ini lebih aman dan tidak mengganggu riwayat data pesanan lama.',
  },
  {
    q: 'Bagaimana cara produk tertentu "tahu" harus masuk ke stasiun mana?',
    a: 'Pengaturan ini dilakukan di halaman Produk (bukan di halaman Stasiun) — setiap produk/kategori bisa di-assign ke satu stasiun. Kalau sebuah produk tidak di-assign ke stasiun manapun, otomatis masuk ke stasiun yang ditandai "Default".',
  },
  {
    q: 'Saya belum punya printer dapur, apakah field "Printer" wajib diisi?',
    a: 'Tidak wajib. Kosongkan saja field Printer kalau Anda belum menyiapkan printer khusus dapur — pesanan tetap bisa dipantau lewat layar Kitchen Display (KDS) tanpa perlu cetak struk fisik.',
  },
  {
    q: 'Kalau saya ubah "Urutan" stasiun, apa dampaknya?',
    a: 'Hanya mengubah urutan tampilan stasiun di daftar dan di tombol pemilih stasiun pada layar Kitchen Display. Tidak memengaruhi data pesanan yang sudah ada.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/f-b-modern" class="text-decoration-none text-medium-emphasis text-body-2">
            F&amp;B Modern
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Stasiun Dapur</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="info" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-restaurant-2-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">
              Stasiun Dapur / Barista
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Atur pembagian kerja dapur supaya pesanan otomatis terarah ke tim yang tepat
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda membuat dan mengatur "stasiun" — pembagian area kerja dapur/bar —
          sebelum menggunakan layar Kitchen Display (KDS).
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="info" />
            Apa itu Stasiun dan Mengapa Penting?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Stasiun</strong> adalah pembagian area kerja di dapur atau bar Anda.
              Bayangkan restoran yang punya meja racik minuman terpisah dari meja masak —
              barista hanya sibuk dengan pesanan minuman, juru masak hanya fokus pesanan
              makanan. Di PintuKasir, pembagian ini disebut "stasiun".
            </p>
            <p class="text-body-2 mb-3">
              Setiap kali ada pesanan masuk, sistem otomatis memisahkan item pesanan sesuai
              stasiun tujuannya. Misalnya pesanan "1 Nasi Goreng + 1 Es Kopi Susu" akan
              muncul di layar stasiun "Dapur Utama" untuk nasi gorengnya, dan di layar
              stasiun "Barista" untuk es kopinya — masing-masing tim cukup lihat
              layar mereka sendiri.
            </p>
            <VAlert type="success" variant="tonal" class="mb-0">
              <strong>Kabar baik:</strong> Kalau usaha Anda kecil dan semua diracik/dimasak
              di satu tempat yang sama, Anda cukup buat <strong>1 stasiun saja</strong>
              dan tandai sebagai "Default" — semua pesanan akan masuk ke situ.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Tambah -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-add-circle-line" class="me-2" color="success" />
            Cara Menambah Stasiun Baru
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Ekstensi F&amp;B → Kitchen Display</strong> di sidebar,
                lalu masuk ke halaman <strong>daftar Stasiun</strong>.
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Tambah Stasiun"</strong> di pojok kanan atas.
              </li>
              <li class="mb-3">
                Isi form: <strong>Nama Stasiun</strong> (contoh: "Barista"), pilih
                <strong>Tipe</strong> yang sesuai, pilih <strong>Printer</strong> kalau
                sudah ada, atur <strong>Urutan</strong> tampil, dan tandai
                <strong>Default</strong>/<strong>Aktif</strong> sesuai kebutuhan.
              </li>
              <li class="mb-0">
                Klik <strong>"Simpan"</strong> — stasiun langsung siap dipakai dan muncul
                sebagai pilihan di layar Kitchen Display. ✅
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Field table -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-list-check-2" class="me-2" color="primary" />
            Penjelasan Setiap Field
          </VCardTitle>
          <VCardText>
            <VTable>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Tipe</th>
                  <th>Wajib?</th>
                  <th>Penjelasan</th>
                  <th>Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in stationFields" :key="row.field">
                  <td><strong>{{ row.field }}</strong></td>
                  <td>{{ row.type }}</td>
                  <td>{{ row.wajib }}</td>
                  <td class="text-body-2">{{ row.desc }}</td>
                  <td class="text-body-2">{{ row.example }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Edit & Hapus -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-edit-line" class="me-2" color="warning" />
            Edit Stasiun
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                Klik ikon <strong>pensil (✏️)</strong> di baris stasiun yang ingin diubah
              </li>
              <li class="mb-2">
                Ubah field yang perlu, lalu klik <strong>"Simpan"</strong>
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-delete-bin-line" class="me-2" color="error" />
            Hapus Stasiun
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                Klik ikon <strong>tempat sampah (🗑️)</strong>, lalu konfirmasi hapus.
              </li>
            </ol>
            <VAlert type="error" variant="tonal" density="compact" class="mt-2 text-body-2">
              <strong>Sebaiknya hindari</strong> menghapus stasiun yang sudah pernah
              menerima pesanan. Nonaktifkan saja lewat toggle "Aktif".
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Mulai sederhana.</strong> Kalau ragu, buat 1 stasiun dulu dan
                  tandai "Default". Anda selalu bisa tambah stasiun lain nanti kalau
                  usaha berkembang.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Beri nama yang jelas untuk tim Anda</strong>, bukan istilah
                  teknis — misalnya "Meja Racik" atau "Dapur Belakang" sesuai istilah
                  yang biasa dipakai karyawan Anda sehari-hari.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Langkah Selanjutnya
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Kitchen Display', sub: 'Pantau pesanan per stasiun secara real-time', icon: 'ri-fire-line', color: 'error', to: '/userguide/f-b-modern/kds' },
              { title: 'Self-Order QR Meja', sub: 'Cara pesanan pelanggan masuk ke stasiun', icon: 'ri-qr-scan-2-line', color: 'primary', to: '/userguide/f-b-modern/self-order' },
              { title: 'Pengaturan Printer', sub: 'Hubungkan stasiun dengan printer dapur', icon: 'ri-printer-line', color: 'secondary', to: '/userguide/sistem/printers' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
